import { useCallback, useEffect, useState } from 'react'
import { getOrgSettings, saveTones, type OrgTone } from '../api/orgSettings'

/**
 * The chips an agent taps at the ticket, beyond the four built in.
 *
 * Its own screen rather than the foot of the wording rules: a team writes
 * these once and comes back to them rarely, and stacked under a textarea
 * they turned one settings page into three unrelated jobs.
 */
export function TonesSection() {
  const [tones, setTones] = useState<OrgTone[]>([])
  const [label, setLabel] = useState('')
  const [instruction, setInstruction] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setTones((await getOrgSettings()).tones ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function write(
    next: Array<{ id?: string; label: string; instruction: string }>,
    { clearForm = false } = {},
  ) {
    setBusy(true)
    setError('')
    try {
      setTones((await saveTones(next)).tones)
      // Only after adding. Clearing on a removal threw away a tone the agent
      // was halfway through typing, which is often why they were removing one.
      if (clearForm) {
        setLabel('')
        setInstruction('')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // The server keeps one tone per label, so adding a name that exists would
  // silently rewrite it. Say so here instead, where the wording can be changed.
  const replacing = tones.some((t) => t.label.trim().toLowerCase() === label.trim().toLowerCase())

  return (
    <div className="card">
      <h2>Your own tones{tones.length > 0 && ` (${tones.length} of 6)`}</h2>
      <p className="hint">Buttons your team taps at a ticket, alongside the four built in.</p>

      {error && <div className="error">{error}</div>}

      <label className="field">
        <span>Button name</span>
        <input
          type="text"
          value={label}
          maxLength={24}
          placeholder="Chase the courier"
          disabled={loading || busy}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>

      <label className="field">
        <span>What it should do</span>
        <textarea
          className="ask"
          rows={2}
          value={instruction}
          maxLength={300}
          placeholder="Apologise for the wait and say we are chasing the courier for an update."
          disabled={loading || busy}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </label>

      <button
        disabled={loading || busy || tones.length >= 6 || !label.trim() || !instruction.trim() || replacing}
        onClick={() =>
          void write([...tones, { label: label.trim(), instruction: instruction.trim() }], { clearForm: true })
        }
      >
        {busy ? 'Saving…' : 'Add tone'}
      </button>

      {replacing && (
        <p className="note">
          You already have a tone called “{label.trim()}”. Remove it first, or use another name.
        </p>
      )}

      {tones.length === 0 ? (
        <p className="note">None yet. Short, Friendly, Formal and Ask for details always show.</p>
      ) : (
        <ul className="docs">
          {tones.map((t) => (
            <li key={t.id}>
              <div className="doc-lines">
                <span className="doc-name">{t.label}</span>
                {/* Two lines, then ellipsis: an instruction is a sentence, and
                    truncating it to one line said almost nothing about it. */}
                <span className="doc-url clamp-2">{t.instruction}</span>
              </div>
              <button
                className="linkish danger"
                disabled={busy}
                onClick={() => void write(tones.filter((x) => x.id !== t.id))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {tones.length >= 6 && <p className="note">Six is the limit. Remove one to add another.</p>}
    </div>
  )
}
